<template>
    <tab-section title="Phases" :full-width="true">
        <a id="prospective-phases" name="prospective-phases"></a>
        <div
            v-for="index in nb_scopes"
            :key="index"
            :class="{ 'uk-margin-large-bottom': (index < nb_scopes) }"
        >
            <table
                class="uk-table prospective-phases"
            >
                <thead v-if="is_scoped">
                    <tr>
                        <th class="uk-width-1-3">
                            <div class="uk-flex uk-flex-middle">
                                <span
                                    class="uk-flex-item-none uk-margin-small-right"
                                >{{ `${(index - 1) * 10}`.padStart(2, '0') }}</span>
                                <form-input
                                    :id="`scope-name-${index}`"
                                    :value="scope(index).name"
                                    name="name"
                                    :required="false"
                                    :editable="editable"
                                    :placeholder="scope_standardized_title(index)"
                                    width="1-1"
                                    class="uk-width-1-1 uk-flex-item-1"
                                    @input="save_scope_title($event, index)"
                                />
                            </div>
                        </th>
                        <th class="uk-width-1-3 uk-text-center">
                            {{ scope_start_date(index) | nibnut.date }} to {{ scope_end_date(index) | nibnut.date }}
                        </th>
                        <th
                            :class="{ 'uk-text-danger': (scope_total(index) !== scope(index).fee) }"
                            class="uk-width-1-3 uk-text-right"
                        >
                            <span>$ {{ scope_total(index) | nibnut.number("0,0") }}</span>
                        </th>
                    </tr>
                </thead>
                <thead v-else>
                    <tr>
                        <th
                            :class="{'uk-text-required': !has_required_data('phase_fees')||!has_required_data('phase_dates')}"
                            class="uk-width-1-3"
                        >
                            <span>
                                <open-icon v-if="!has_required_data('phase_fees')||!has_required_data('phase_dates')" glyph="exclamation-triangle" />
                                Phases
                            </span>
                        </th>
                        <th v-if="!!phases.length" class="uk-width-1-3 uk-text-center">
                            <open-icon v-if="!!record && !record.projected_starts_at" glyph="exclamation-triangle" class="uk-text-required uk-margin-small-right" />
                            {{ record.computed_starts_at | nibnut.date }} to {{ record.computed_ends_at | nibnut.date }}
                        </th>
                        <th v-if="!!phases.length" class="uk-width-1-3 uk-text-right">
                            <span>$ {{ (record.activated_at ? record.computed_fee : record.computed_prospective_fee) | nibnut.number("0,0") }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="!phases.length">
                    <tr>
                        <td colspan="3" class="lam-phases-empty-state uk-text-center">
                            <div class="uk-text-muted">(No phases defined)</div>
                            <base-button
                                v-if="editable"
                                class="uk-margin-top"
                                @click.prevent="add_phases_for_scope(index)"
                            >
                                <open-icon glyph="plus" class="uk-margin-small-right" />
                                <span>Add Phase(s)<span v-if="is_scoped"> to {{ scope(index).name || scope_standardized_title(index) }}</span>...</span>
                            </base-button>
                        </td>
                    </tr>
                </tbody>
                <draggable
                    ref="tbody"
                    :id="`scope-${index}`"
                    :list="scope_phases(index)"
                    tag="tbody"
                    group="phases"
                    :sort="false"
                    handle=".lam-drag-handle"
                    draggable="tr"
                    ghostClass="lam-dropzone"
                    dragClass="lam-dragging"
                    :disabled="!editable"
                    @add="drag_add"
                >
                <!--<tbody v-else ref="tbody">//-->
                    <phase
                        v-for="phase in scope_phases(index)"
                        :key="phase.id"
                        :id="`phase-${phase.id}`"
                        :project="record"
                        :record="phase"
                        :editable="editable"
                    />
                <!--</tbody>//-->
                </draggable>
            </table>
            <button
                v-if="!record.activated_at && (editable && !!phases.length)"
                type="button"
                :class="{'lam-lower-contrast': !!wizardShown}"
                class="uk-button uk-button-primary uk-width-1-1"
                @click.stop="add_phases_for_scope(index)"
            >
                Add new phase(s)<span v-if="is_scoped"> to {{ scope(index).name || scope_standardized_title(index) }}</span>
            </button>
        </div>

        <project-phases-wizard
            v-if="!!record"
            :show.sync="adding_phases"
            :record="record"
            :scope-id="adding_to_scope_id"
        />
    </tab-section>
</template>

<script>
import { orderBy } from "lodash"

import is_project_tab_section from "../mixins/IsProjectTabSection"
import project_utilities from "../../../mixins/ProjectUtilities"
import handles_phase_builder_selection from "../../../mixins/HandlesPhaseBuilderSelection"
import handles_active_row from "../../../mixins/HandlesActiveRow"
import handles_saving from "../../../mixins/HandlesSaving"
import wizard_target from "../mixins/WizardTarget"

import FormInput from "../../../ui/FormInput"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import Phase from "./Phase"
import ProjectPhasesWizard from "../../../dialogs/ProjectPhasesWizard"

import draggable from "vuedraggable"

export default {
    mixins: [is_project_tab_section, project_utilities, handles_phase_builder_selection, handles_active_row, handles_saving, wizard_target],
    components: {
        FormInput,
        BaseButton,
        OpenIcon,
        Phase,
        ProjectPhasesWizard,
        draggable
    },
    mounted () {
        this.reset_record_id()
    },
    watch: {
        "record.id": "reset_record_id"
    },
    methods: {
        reset_record_id () {
            this.record_id = this.record ? this.record.id : 0
        },
        activate_row (row) {
            this.frozen_row_state = !!row
        },
        scope (index) {
            if(this.scopes.length) return this.scopes[index - 1] || {}
            return {}
        },
        scope_standardized_title (index) {
            const number = `${(index - 1) * 10}`.padStart(2, "0")
            return `Scope ${number}`
        },
        scope_start_date (index) {
            let start_date = null
            this.scope_phases(index).forEach(phase => {
                if(!start_date) start_date = this.$moment(phase.starts_at)
                else start_date = this.$moment.min(start_date, this.$moment(phase.starts_at))
            })
            return start_date
        },
        scope_end_date (index) {
            let end_date = null
            this.scope_phases(index).forEach(phase => {
                if(!end_date) end_date = this.$moment(phase.ends_at)
                else end_date = this.$moment.max(end_date, this.$moment(phase.ends_at))
            })
            return end_date
        },
        scope_total (index) {
            return this.scope_phases(index).reduce((total, phase) => {
                return total + phase.prospective_fee
            }, 0)
        },
        save_scope_title (title, index) {
            if(title === this.scope_standardized_title(index)) title = ""
            else if(title === "") title = this.scope_standardized_title(index)

            this.record.scopes_data.scopes[index - 1].name = title

            this.$store.dispatch("RECORD_SAVE", {
                entity: "proposal_revision",
                id: this.record.scopes_data.id,
                data: { scopes: this.record.scopes_data.scopes }
            }).catch(error => {
                this.$error(error.message)
            })
        },
        scope_phases (index) {
            if(!this.is_scoped) return this.rows
            const scope_id = index - 1
            return this.rows.filter(row => row.scope_id === scope_id)
        },
        add_phases_for_scope (index) {
            this.adding_to_scope_id = this.is_scoped ? index - 1 : null
            this.adding_phases = true
        },
        drag_add (drag_event) {
            const phase_id = parseInt(drag_event.clone.id.replace(/^phase-/, ""))

            const scope = parseInt(drag_event.to.id.replace(/^scope-/, "")) - 1
            if(!phase_id || (scope < 0)) return

            this.$store.dispatch("RECORD_SAVE", {
                entity: "phase",
                id: phase_id,
                data: { scope_id: scope }
            }).catch(error => {
                this.$error(error.message)
            })
        }
    },
    computed: {
        is_scoped () {
            return !!this.scopes.length
        },
        nb_scopes () {
            if(this.is_scoped) return this.scopes.length
            return 1
        },
        rows () {
            let rows
            if(!this.frozen_row_state || !this.frozen_row_ids) {
                rows = orderBy(this.phases, "starts_at", "asc")
                // eslint-disable-next-line
                this.frozen_row_ids = rows.map(row => row.id)
            } else {
                rows = this.frozen_row_ids.reduce((rows, row_id) => {
                    const row = this.phases.find(row => row.id === row_id)
                    if(row) rows.push(row)
                    return rows
                }, [])
            }
            return rows
        },
        scopes () {
            if(this.record && this.record.scopes_data) return this.record.scopes_data.scopes || []
            return []
        },
        end_date () {
            if(!this.phases.length) return ""
            const last_phase = orderBy(this.phases, "ends_at", "desc")[0]
            return last_phase.ends_at
        }
    },
    data () {
        return {
            entity: "project",

            frozen_row_state: false,
            frozen_row_ids: null,

            adding_to_scope_id: null
        }
    }
}
</script>

<style lang="less">
@import "@/assets/less/variables";

.uk-table.prospective-phases {
    .lam-phase-title-field-container {
        .lam-overridable,
        .overridable-field {
            input {
                padding-top: 0;
                padding-bottom: 0;
                border: 0;
            }
            .overridable-field-button {
                font-size: 14px;
            }
            .overridable-field-button + .overridable-field-button {
                margin-left: 15px;
            }
        }
    }
    thead {
        padding: 15px 15px;
        font-size: 20px;
        background-color: @global-titles-background;
        margin: 0;

        th {
            vertical-align: middle;

            input {
                font-size: 20px;
            }
            span {
                line-height: 25px;
            }
        }
    }
    tbody {
        td.lam-phases-empty-state {
            padding: 3rem 1rem;
        }
        td.prospective-scope-phases {
            padding-left: 0;
            padding-right: 0;

            & > table.uk-table {
                thead {
                    background-color: transparent;
                }
            }
        }
    }
}
</style>
